import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useRef, type FormEvent, type KeyboardEvent, type MouseEvent } from "react";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";

interface Props {
  submitButtonText?: string | null;
  cancelButtonText?: string;
  hideSubmitButton?: boolean;
  hideCancelButton?: boolean;
  hideDialogActions?: boolean;
  onSubmit?: () => void;
  submitFormId?: string;
  isSubmitting?: boolean;
  open: boolean;
  title?: string;
  onClose?: () => void;
  children?: any;
  presetValues?: any;
  disableSubmit?: boolean;
  disableCancelButton?: boolean;
  contentStyle?: React.CSSProperties;
  contentClassName?: string;
  maxWidth?: string;
  paperBorderRadius?: string;
  buttonPlacement?:
    | "center"
    | "space-around"
    | "space-evenly"
    | "space-between"
    | "flex-end"
    | string
    | null
    | undefined;
}

export default function Modal(props: Props) {
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const paperRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  let submitButtonText = t("save");
  let cancelButtonText = t("cancel");

  const isOwnDialogTarget = (target: EventTarget) => {
    const paper = paperRef.current;
    return (
      target instanceof Element &&
      paper !== null &&
      paper.contains(target) &&
      target.closest('[role="dialog"], [role="alertdialog"]') === paper
    );
  };

  const isSubmissionBlocked = () =>
    !props.open ||
    props.disableSubmit ||
    props.isSubmitting ||
    !paperRef.current ||
    Boolean(paperRef.current.closest('[hidden], [inert], [aria-hidden="true"]'));

  const handleSubmitClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (isSubmissionBlocked()) {
      event.preventDefault();
      return;
    }

    if (props.submitFormId) {
      const form = event.currentTarget.form;
      if (!form || !isOwnDialogTarget(form)) {
        event.preventDefault();
      }
      return;
    }

    props.onSubmit?.();
  };

  const handleSubmitCapture = (event: FormEvent) => {
    if (!isOwnDialogTarget(event.target)) {
      return;
    }
    if (isSubmissionBlocked()) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      event.key !== "Enter" ||
      event.defaultPrevented ||
      event.nativeEvent.isComposing ||
      event.nativeEvent.keyCode === 229 ||
      event.repeat ||
      event.altKey || event.ctrlKey || event.metaKey || event.shiftKey ||
      !isOwnDialogTarget(event.target) ||
      isSubmissionBlocked() ||
      props.submitFormId ||
      !props.onSubmit ||
      props.hideSubmitButton ||
      props.hideDialogActions
    ) {
      return;
    }

    const target = event.target as Element;
    if (target.closest([
      "form", "textarea", "select", "button", "a[href]",
      'input[type="button"]', 'input[type="submit"]', 'input[type="reset"]',
      '[contenteditable]:not([contenteditable="false"])',
      '[role="combobox"]', '[role="listbox"]', '[role="option"]',
      '[role="spinbutton"]', '[role="menu"]', '[role="menuitem"]',
      '[role="button"]', '[aria-multiline="true"]',
    ].join(", "))) {
      return;
    }

    event.preventDefault();
    submitButtonRef.current?.click();
  };

  if (props.submitButtonText) {
    submitButtonText = props.submitButtonText;
  }

  if (props.cancelButtonText) {
    cancelButtonText = props.cancelButtonText;
  }

  let submitButton = <></>;

  if (!props.hideSubmitButton) {
    submitButton = (
      <>
        <GenericButton
          ref={submitButtonRef}
          variant="contained"
          disabled={props.disableSubmit}
          loading={props.isSubmitting}
          showSpinnerOnDisable
          type={props.submitFormId ? "submit" : "button"}
          form={props.submitFormId}
          onClick={handleSubmitClick}
        >
          {submitButtonText}
        </GenericButton>
      </>
    );
  }

  let cancelButton = <></>;

  if (!props.hideCancelButton) {
    cancelButton = (
      <GenericButton
        onClick={props.onClose}
        disabled={props.disableCancelButton}
        variant="outlined"
      >
        {cancelButtonText}
      </GenericButton>
    );
  }

  let dialogActions = <></>;

  if (!props.hideDialogActions) {
    dialogActions = (
      <DialogActions
        sx={{
          p: "1.25rem",
          justifyContent: props.buttonPlacement
            ? props.buttonPlacement
            : "space-between",
        }}
      >
        {cancelButton}
        {submitButton}
      </DialogActions>
    );
  }

  const dismissOnEscape =
    props.onClose ??
    (props.hideCancelButton && !props.submitFormId ? props.onSubmit : undefined);

  const cancelClose = useModalDismiss(props.open, dismissOnEscape, {
    enabled: !props.disableCancelButton,
  });

  return (
    <Dialog
      open={props.open}
      onKeyDown={handleKeyDown}
      onSubmitCapture={handleSubmitCapture}
      {...cancelClose}
      PaperProps={{
        ref: paperRef,
        sx: {
          borderRadius: `${props.paperBorderRadius ?? "0.25rem"} !important`,
          width: "100%",
          maxWidth: isMobile ? "100vw" : (props.maxWidth ?? "80vw"),
        },
      }}
    >
      <DialogTitle textAlign="center">{props.title}</DialogTitle>
      <DialogContent className={props.contentClassName} sx={props.contentStyle}>
        {props.children}
      </DialogContent>
      {dialogActions}
    </Dialog>
  );
}
